import { useState, useRef, useCallback } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { useI18n } from '../i18n'
import { useAuthStore } from '../store/useAuthStore'
import { useQrStore } from '../store/useQrStore'
import { CopyField } from '../components/CopyField'

type QrType = 'link' | 'text' | 'email' | 'call' | 'sms' | 'vcard' | 'whatsapp' | 'wifi' | 'instagram' | 'pdf' | 'app' | 'images' | 'video' | 'social' | 'event' | 'barcode'
type DownloadFormat = 'png' | 'gif' | 'svg' | 'jpg'
type WifiEncryption = 'WPA' | 'WEP' | 'nopass'

interface WifiState { ssid: string; encryption: WifiEncryption; password: string; hidden: boolean }
interface WhatsAppState { countryCode: string; phone: string; message: string }
interface VCardState {
  firstName: string; lastName: string; phone: string; mobile: string
  email: string; website: string; company: string; jobTitle: string
  fax: string; address: string; city: string; zip: string; country: string
}
interface InstaState { username: string }

const QR_TYPES: { key: QrType; icon: string; labelKey: string; active: boolean }[] = [
  { key: 'link',      icon: 'URL', labelKey: 'qr.typeLink',      active: true },
  { key: 'wifi',      icon: 'NET', labelKey: 'qr.typeWifi',      active: true },
  { key: 'whatsapp',  icon: 'WA',  labelKey: 'qr.typeWhatsapp',  active: true },
  { key: 'vcard',     icon: 'VCF', labelKey: 'qr.typeVcard',     active: true },
  { key: 'instagram', icon: 'IG',  labelKey: 'qr.typeInstagram', active: true },
  { key: 'text',      icon: 'TXT', labelKey: 'qr.typeText',      active: true },
  { key: 'email',     icon: '@',   labelKey: 'qr.typeEmail',     active: false },
  { key: 'call',      icon: 'TEL', labelKey: 'qr.typeCall',      active: false },
  { key: 'sms',       icon: 'SMS', labelKey: 'qr.typeSms',       active: false },
  { key: 'pdf',       icon: 'PDF', labelKey: 'qr.typePdf',       active: false },
  { key: 'app',       icon: 'APP', labelKey: 'qr.typeApp',       active: false },
  { key: 'images',    icon: 'IMG', labelKey: 'qr.typeImages',    active: false },
  { key: 'video',     icon: 'VID', labelKey: 'qr.typeVideo',     active: false },
  { key: 'social',    icon: 'SOC', labelKey: 'qr.typeSocial',    active: false },
  { key: 'event',     icon: 'CAL', labelKey: 'qr.typeEvent',     active: false },
  { key: 'barcode',   icon: '|||', labelKey: 'qr.typeBarcode',   active: false },
]

const DESIGN_TABS = ['qr.designFrame', 'qr.designShape', 'qr.designLogo'] as const
const REDIRECT_BASE = 'https://qr-api.masterlevel.de/r/'

function timestamp(): string {
  const d = new Date()
  return d.getFullYear().toString() +
    String(d.getMonth() + 1).padStart(2, '0') +
    String(d.getDate()).padStart(2, '0') +
    String(d.getHours()).padStart(2, '0') +
    String(d.getMinutes()).padStart(2, '0')
}

function downloadSvgElement(svg: SVGSVGElement, format: DownloadFormat) {
  const serializer = new XMLSerializer()
  const source = serializer.serializeToString(svg)

  if (format === 'svg') {
    const blob = new Blob([source], { type: 'image/svg+xml' })
    triggerDownload(URL.createObjectURL(blob), `qrcode-${timestamp()}.svg`)
    return
  }

  const svgBlob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' })
  const svgUrl = URL.createObjectURL(svgBlob)
  const img = new Image()

  img.onload = () => {
    const canvas = document.createElement('canvas')
    canvas.width = 400
    canvas.height = 400
    const ctx = canvas.getContext('2d')!
    if (format === 'jpg') {
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
    }
    ctx.drawImage(img, 0, 0, 400, 400)
    URL.revokeObjectURL(svgUrl)
    const mimeType = format === 'jpg' ? 'image/jpeg' : format === 'gif' ? 'image/gif' : 'image/png'
    canvas.toBlob((blob) => {
      if (!blob) return
      triggerDownload(URL.createObjectURL(blob), `qrcode-${timestamp()}.${format}`)
    }, mimeType)
  }
  img.src = svgUrl
}

function triggerDownload(href: string, filename: string) {
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  a.click()
  URL.revokeObjectURL(href)
}

function buildVCard(vc: VCardState): string {
  const lines = ['BEGIN:VCARD', 'VERSION:3.0']
  if (vc.firstName || vc.lastName) {
    lines.push(`N:${vc.lastName};${vc.firstName};;;`)
    lines.push(`FN:${[vc.firstName, vc.lastName].filter(Boolean).join(' ')}`)
  }
  if (vc.company)  lines.push(`ORG:${vc.company}`)
  if (vc.jobTitle) lines.push(`TITLE:${vc.jobTitle}`)
  if (vc.phone)    lines.push(`TEL;TYPE=WORK,VOICE:${vc.phone}`)
  if (vc.mobile)   lines.push(`TEL;TYPE=CELL:${vc.mobile}`)
  if (vc.fax)      lines.push(`TEL;TYPE=FAX:${vc.fax}`)
  if (vc.email)    lines.push(`EMAIL:${vc.email}`)
  if (vc.website)  lines.push(`URL:${vc.website}`)
  if (vc.address || vc.city || vc.zip || vc.country) {
    lines.push(`ADR;TYPE=WORK:;;${vc.address};${vc.city};;${vc.zip};${vc.country}`)
  }
  lines.push('END:VCARD')
  return lines.join('\n')
}

function buildQrValue(
  type: QrType,
  url: string,
  text: string,
  wifi: WifiState,
  wa: WhatsAppState,
  vc: VCardState,
  insta: InstaState,
  savedShortCode: string | null
): string {
  if (type === 'link') {
    return savedShortCode ? REDIRECT_BASE + savedShortCode : url || 'https://qr.masterlevel.de'
  }
  switch (type) {
    case 'text':
      return text || 'https://qr.masterlevel.de'
    case 'wifi':
      return `WIFI:T:${wifi.encryption};S:${wifi.ssid};P:${wifi.password};H:${wifi.hidden ? 'true' : 'false'};;`
    case 'whatsapp': {
      const num = (wa.countryCode + wa.phone).replace(/\D/g, '')
      return `https://wa.me/${num}${wa.message ? '?text=' + encodeURIComponent(wa.message) : ''}`
    }
    case 'vcard':
      return buildVCard(vc)
    case 'instagram':
      return insta.username ? `https://instagram.com/${insta.username}` : 'https://qr.masterlevel.de'
    default:
      return 'https://qr.masterlevel.de'
  }
}

function hasContent(type: QrType, url: string, text: string, wifi: WifiState, wa: WhatsAppState, vc: VCardState, insta: InstaState): boolean {
  switch (type) {
    case 'link':      return url.length > 0
    case 'text':      return text.length > 0
    case 'wifi':      return wifi.ssid.length > 0
    case 'whatsapp':  return wa.phone.length > 0
    case 'vcard':     return (vc.firstName + vc.lastName + vc.phone + vc.email).length > 0
    case 'instagram': return insta.username.length > 0
    default:          return false
  }
}

const INPUT_CLASS = 'rounded-lg border-2 border-surface-lighter bg-surface px-4 py-3 text-text outline-none transition-colors focus:border-primary w-full'
const LABEL_CLASS = 'text-sm text-text-muted'

export function QrBuilderPage() {
  const { t } = useI18n()
  const user = useAuthStore((s) => s.user)
  const createQr = useQrStore((s) => s.create)

  const [selectedType, setSelectedType] = useState<QrType>('link')
  const [url, setUrl] = useState('')
  const [wifi, setWifi] = useState<WifiState>({ ssid: '', encryption: 'WPA', password: '', hidden: false })
  const [wa, setWa] = useState<WhatsAppState>({ countryCode: '', phone: '', message: '' })
  const [vc, setVc] = useState<VCardState>({ firstName: '', lastName: '', phone: '', mobile: '', email: '', website: '', company: '', jobTitle: '', fax: '', address: '', city: '', zip: '', country: '' })
  const [insta, setInsta] = useState<InstaState>({ username: '' })
  const [text, setText] = useState('')

  const [designTab, setDesignTab] = useState<typeof DESIGN_TABS[number]>('qr.designFrame')
  const [downloadFormat, setDownloadFormat] = useState<DownloadFormat>('svg')
  const [saving, setSaving] = useState(false)
  const [savedShortCode, setSavedShortCode] = useState<string | null>(null)
  const qrRef = useRef<SVGSVGElement>(null)

  const qrValue = buildQrValue(selectedType, url, text, wifi, wa, vc, insta, savedShortCode)
  const contentReady = hasContent(selectedType, url, text, wifi, wa, vc, insta)

  const selectType = (type: QrType) => {
    setSelectedType(type)
    setSavedShortCode(null)
  }

  const handleCreateShortLink = useCallback(async () => {
    if (!user || selectedType !== 'link' || !url || savedShortCode) return
    setSaving(true)
    const code = await createQr(url)
    setSaving(false)
    if (code) setSavedShortCode(code.short_code)
  }, [user, selectedType, url, savedShortCode, createQr])

  const handleDownload = useCallback(async () => {
    const svg = qrRef.current
    if (!svg || !contentReady) return

    if (selectedType === 'link' && user && !savedShortCode) {
      setSaving(true)
      const code = await createQr(url)
      setSaving(false)
      if (code) {
        setSavedShortCode(code.short_code)
        setTimeout(() => {
          if (qrRef.current) downloadSvgElement(qrRef.current, downloadFormat)
        }, 100)
        return
      }
    }

    downloadSvgElement(svg, downloadFormat)
  }, [selectedType, url, user, savedShortCode, createQr, downloadFormat, contentReady])

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-8 flex flex-col gap-8">

      {/* Step 1: Type selection */}
      <section className="rounded-xl bg-surface-light p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-text-muted">
          1 &mdash; {t('qr.stepType')}
        </h2>
        <div className="flex flex-wrap gap-2">
          {QR_TYPES.map(({ key, icon, labelKey, active }) => (
            <button
              key={key}
              onClick={() => active && selectType(key)}
              disabled={!active}
              className={`flex items-center gap-2 rounded-lg border-2 px-4 py-2 text-sm font-medium transition-colors ${
                selectedType === key
                  ? 'border-primary bg-primary/10 text-primary'
                  : active
                    ? 'border-surface-lighter bg-surface text-text-muted hover:border-primary/50 hover:text-text'
                    : 'border-surface-lighter bg-surface text-text-muted/40 cursor-not-allowed'
              }`}
            >
              <span>{icon}</span>
              <span>{t(labelKey as Parameters<typeof t>[0])}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Step 2: Content */}
      <section className="rounded-xl bg-surface-light p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-text-muted">
          2 &mdash; {t('qr.stepContent')}
        </h2>

        {/* Link */}
        {selectedType === 'link' && (
          <div className="flex flex-col gap-3">
            <label className={LABEL_CLASS}>{t('qr.enterUrl')}</label>
            <input
              type="url"
              placeholder={t('qr.urlPlaceholder')}
              value={url}
              onChange={(e) => { setUrl(e.target.value); setSavedShortCode(null) }}
              className={INPUT_CLASS}
            />
          </div>
        )}

        {/* Text */}
        {selectedType === 'text' && (
          <div className="flex flex-col gap-3">
            <label className={LABEL_CLASS}>{t('qr.text.content')}</label>
            <textarea
              placeholder={t('qr.text.placeholder')}
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={5}
              className={INPUT_CLASS + ' resize-none'}
            />
          </div>
        )}

        {/* WiFi */}
        {selectedType === 'wifi' && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="flex flex-col gap-2">
                <label className={LABEL_CLASS}>{t('qr.wifi.networkName')}</label>
                <input
                  type="text"
                  placeholder="SSID"
                  value={wifi.ssid}
                  onChange={(e) => setWifi({ ...wifi, ssid: e.target.value })}
                  className={INPUT_CLASS}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className={LABEL_CLASS}>{t('qr.wifi.encryption')}</label>
                <select
                  value={wifi.encryption}
                  onChange={(e) => setWifi({ ...wifi, encryption: e.target.value as WifiEncryption })}
                  className={INPUT_CLASS}
                >
                  <option value="WPA">{t('qr.wifi.encWpa')}</option>
                  <option value="WEP">{t('qr.wifi.encWep')}</option>
                  <option value="nopass">{t('qr.wifi.encNone')}</option>
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label className={LABEL_CLASS}>{t('qr.wifi.password')}</label>
                <input
                  type="text"
                  placeholder={t('qr.wifi.password')}
                  value={wifi.password}
                  onChange={(e) => setWifi({ ...wifi, password: e.target.value })}
                  disabled={wifi.encryption === 'nopass'}
                  className={INPUT_CLASS + (wifi.encryption === 'nopass' ? ' opacity-40' : '')}
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-text-muted cursor-pointer">
              <input
                type="checkbox"
                checked={wifi.hidden}
                onChange={(e) => setWifi({ ...wifi, hidden: e.target.checked })}
                className="accent-primary"
              />
              {t('qr.wifi.hidden')}
            </label>
          </div>
        )}

        {/* WhatsApp */}
        {selectedType === 'whatsapp' && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label className={LABEL_CLASS}>{t('qr.wa.countryCode')}</label>
                <input
                  type="text"
                  placeholder="+49"
                  value={wa.countryCode}
                  onChange={(e) => setWa({ ...wa, countryCode: e.target.value })}
                  className={INPUT_CLASS}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className={LABEL_CLASS}>{t('qr.wa.phone')}</label>
                <input
                  type="tel"
                  placeholder="1511 2345678"
                  value={wa.phone}
                  onChange={(e) => setWa({ ...wa, phone: e.target.value })}
                  className={INPUT_CLASS}
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label className={LABEL_CLASS}>{t('qr.wa.message')}</label>
              <textarea
                placeholder={t('qr.wa.message')}
                value={wa.message}
                onChange={(e) => setWa({ ...wa, message: e.target.value })}
                rows={3}
                className={INPUT_CLASS + ' resize-none'}
              />
            </div>
          </div>
        )}

        {/* vCard */}
        {selectedType === 'vcard' && (
          <div className="flex flex-col gap-6">
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-muted">{t('qr.vcard.contactInfo')}</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <label className={LABEL_CLASS}>{t('qr.vcard.firstName')}</label>
                  <input type="text" value={vc.firstName} onChange={(e) => setVc({ ...vc, firstName: e.target.value })} className={INPUT_CLASS} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className={LABEL_CLASS}>{t('qr.vcard.lastName')}</label>
                  <input type="text" value={vc.lastName} onChange={(e) => setVc({ ...vc, lastName: e.target.value })} className={INPUT_CLASS} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className={LABEL_CLASS}>{t('qr.vcard.phone')}</label>
                  <input type="tel" value={vc.phone} onChange={(e) => setVc({ ...vc, phone: e.target.value })} className={INPUT_CLASS} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className={LABEL_CLASS}>{t('qr.vcard.mobile')}</label>
                  <input type="tel" value={vc.mobile} onChange={(e) => setVc({ ...vc, mobile: e.target.value })} className={INPUT_CLASS} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className={LABEL_CLASS}>{t('qr.vcard.email')}</label>
                  <input type="email" value={vc.email} onChange={(e) => setVc({ ...vc, email: e.target.value })} className={INPUT_CLASS} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className={LABEL_CLASS}>{t('qr.vcard.website')}</label>
                  <input type="url" placeholder="https://" value={vc.website} onChange={(e) => setVc({ ...vc, website: e.target.value })} className={INPUT_CLASS} />
                </div>
              </div>
            </div>
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-muted">{t('qr.vcard.companyInfo')}</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-2 sm:col-span-2">
                  <label className={LABEL_CLASS}>{t('qr.vcard.company')}</label>
                  <input type="text" value={vc.company} onChange={(e) => setVc({ ...vc, company: e.target.value })} className={INPUT_CLASS} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className={LABEL_CLASS}>{t('qr.vcard.jobTitle')}</label>
                  <input type="text" value={vc.jobTitle} onChange={(e) => setVc({ ...vc, jobTitle: e.target.value })} className={INPUT_CLASS} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className={LABEL_CLASS}>{t('qr.vcard.fax')}</label>
                  <input type="tel" value={vc.fax} onChange={(e) => setVc({ ...vc, fax: e.target.value })} className={INPUT_CLASS} />
                </div>
              </div>
            </div>
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-muted">{t('qr.vcard.location')}</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-2 sm:col-span-2">
                  <label className={LABEL_CLASS}>{t('qr.vcard.address')}</label>
                  <input type="text" value={vc.address} onChange={(e) => setVc({ ...vc, address: e.target.value })} className={INPUT_CLASS} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className={LABEL_CLASS}>{t('qr.vcard.city')}</label>
                  <input type="text" value={vc.city} onChange={(e) => setVc({ ...vc, city: e.target.value })} className={INPUT_CLASS} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className={LABEL_CLASS}>{t('qr.vcard.zip')}</label>
                  <input type="text" value={vc.zip} onChange={(e) => setVc({ ...vc, zip: e.target.value })} className={INPUT_CLASS} />
                </div>
                <div className="flex flex-col gap-2 sm:col-span-2">
                  <label className={LABEL_CLASS}>{t('qr.vcard.country')}</label>
                  <input type="text" value={vc.country} onChange={(e) => setVc({ ...vc, country: e.target.value })} className={INPUT_CLASS} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Instagram */}
        {selectedType === 'instagram' && (
          <div className="flex flex-col gap-3">
            <label className={LABEL_CLASS}>{t('qr.insta.username')}</label>
            <div className="flex items-center gap-0">
              <span className="rounded-l-lg border-2 border-r-0 border-surface-lighter bg-surface-light px-4 py-3 text-text-muted text-sm select-none">@</span>
              <input
                type="text"
                placeholder="username"
                value={insta.username}
                onChange={(e) => setInsta({ username: e.target.value.replace(/^@/, '') })}
                className="rounded-r-lg border-2 border-surface-lighter bg-surface px-4 py-3 text-text outline-none transition-colors focus:border-primary flex-1"
              />
            </div>
          </div>
        )}

        {/* Coming soon for inactive types */}
        {!['link', 'text', 'wifi', 'whatsapp', 'vcard', 'instagram'].includes(selectedType) && (
          <p className="text-sm text-text-muted">Coming soon...</p>
        )}
      </section>

      {/* Step 3: Design + Preview */}
      <div className="flex flex-col gap-6 lg:flex-row">
        <section className="flex-1 rounded-xl bg-surface-light p-6">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-text-muted">
            3 &mdash; {t('qr.stepDesign')}
          </h2>
          <div className="mb-4 flex gap-2">
            {DESIGN_TABS.map((tab) => (
              <button
                key={tab}
                onClick={() => setDesignTab(tab)}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  designTab === tab
                    ? 'bg-primary text-white'
                    : 'bg-surface text-text-muted hover:text-text'
                }`}
              >
                {t(tab as Parameters<typeof t>[0])}
              </button>
            ))}
          </div>
          <p className="text-sm text-text-muted">Coming soon...</p>
        </section>

        {/* QR Preview */}
        <section className="flex flex-col items-center gap-4 rounded-xl bg-surface-light p-6">
          <div className="rounded-xl bg-white p-4">
            <QRCodeSVG
              ref={qrRef}
              value={qrValue}
              size={200}
              level="M"
            />
          </div>
          {selectedType === 'link' && contentReady && savedShortCode && (
            <div className="w-full flex flex-col gap-1.5">
              <span className="text-xs text-text-muted">{t('qr.scanUrl')}</span>
              <CopyField value={REDIRECT_BASE + savedShortCode} className="w-full" />
            </div>
          )}
          {selectedType === 'link' && contentReady && user && !savedShortCode && (
            <button
              onClick={handleCreateShortLink}
              disabled={saving}
              className="w-full rounded-lg border-2 border-primary py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/10 disabled:opacity-40"
            >
              {saving ? t('general.loading') : t('qr.createShortLink')}
            </button>
          )}
          <div className="flex gap-4">
            {(['png', 'gif', 'svg', 'jpg'] as DownloadFormat[]).map((fmt) => (
              <label key={fmt} className="flex items-center gap-1.5 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="downloadFormat"
                  value={fmt}
                  checked={downloadFormat === fmt}
                  onChange={() => setDownloadFormat(fmt)}
                  className="accent-primary"
                />
                {fmt.toUpperCase()}
              </label>
            ))}
          </div>
          <button
            onClick={handleDownload}
            disabled={!contentReady || saving}
            className="w-full rounded-lg bg-primary py-3 font-medium text-white transition-colors hover:bg-primary-dark disabled:opacity-40"
          >
            {saving ? t('general.loading') : t('qr.download')}
          </button>
          {!user && (
            <p className="text-xs text-text-muted text-center">
              <a href="/login" className="text-primary hover:underline">{t('auth.login')}</a>
              {' '}&mdash; um Scan-Statistiken zu tracken
            </p>
          )}
        </section>
      </div>

    </div>
  )
}
